import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { NetworkSummary as OriginalNetworkSummary } from '../api/types';
import { API_CONFIG } from '../api/config';

const REFETCH_INTERVAL_MS = 1 * 60 * 1000; // Check every 1 minute
const MAX_DATA_AGE_MS = 3 * 60 * 1000; // Refetch if data is older than 3 minutes

interface DbNetworkSummaryStats {
  id?: number;
  block_count: number | null;
  difficulty: number | null;
  difficulty_hybrid: string | null;
  supply: number | null;
  hashrate: string | null;
  last_btc_price: number | null;
  connections: number | null;
  masternode_count_online: number | null;
  masternode_count_offline: number | null;
  last_updated_at?: string | null;
}

interface NetworkContextType {
  data: OriginalNetworkSummary;
  loading: boolean;
  error: Error | null;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

const transformDbDataToNetworkSummary = (dbData: DbNetworkSummaryStats | null): OriginalNetworkSummary => {
  return {
    blockcount: dbData?.block_count ?? API_CONFIG.FALLBACK.BLOCK_HEIGHT,
    difficulty: dbData?.difficulty ?? API_CONFIG.FALLBACK.DIFFICULTY,
    difficultyHybrid: dbData?.difficulty_hybrid ?? "",
    supply: dbData?.supply ?? API_CONFIG.FALLBACK.MONEY_SUPPLY,
    hashrate: String(dbData?.hashrate ?? API_CONFIG.FALLBACK.HASHRATE),
    lastPrice: dbData?.last_btc_price ?? API_CONFIG.FALLBACK.PRICE.BTC,
    connections: dbData?.connections ?? API_CONFIG.FALLBACK.CONNECTIONS,
    masternodeCountOnline: dbData?.masternode_count_online ?? API_CONFIG.FALLBACK.MASTERNODES.ONLINE,
    masternodeCountOffline: dbData?.masternode_count_offline ?? API_CONFIG.FALLBACK.MASTERNODES.OFFLINE,
  };
};

export function NetworkProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<OriginalNetworkSummary>(() => transformDbDataToNetworkSummary(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState<number>(0);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!isInitialLoad) {
      console.log('[NetworkContext] Background refetch triggered...');
    }
    setError(null);
    try {
      const { data: dbData, error: dbError } = await supabase
        .from('network_summary_stats')
        .select('*')
        .eq('id', 1)
        .single<DbNetworkSummaryStats>();

      if (dbError) {
        console.error('[NetworkContext] Error fetching network summary from Supabase:', dbError);
        // Keep using current/fallback data on error for background refresh
        if (isInitialLoad) setData(transformDbDataToNetworkSummary(null)); 
        setError(new Error('Failed to fetch network data: ' + dbError.message));
      } else if (dbData) {
        setData(transformDbDataToNetworkSummary(dbData));
        setLastFetchTimestamp(Date.now());
        console.log('[NetworkContext] Fetched network summary data');
      } else {
        if (isInitialLoad) setData(transformDbDataToNetworkSummary(null));
        setError(new Error('No network summary data found in database.'));
      }
    } catch (err) {
      console.error('[NetworkContext] Catch block error fetching network data:', err);
      if (isInitialLoad) setData(transformDbDataToNetworkSummary(null));
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch and real-time subscription
  useEffect(() => {
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      // Initial fetch
      setLoading(true);
      await fetchData(true);

      if (!mounted) return;

      // Set up real-time subscription
      channel = supabase
        .channel('public:network_summary_stats')
        .on(
          'postgres_changes',
          { 
            event: '*', 
            schema: 'public', 
            table: 'network_summary_stats',
            filter: 'id=eq.1' // Only listen for updates to the single row
          },
          (payload) => {
            if (!mounted) return;
            console.log('[NetworkContext] Realtime update received:', payload);
            
            // Handle different event types
            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
              const newData = payload.new as DbNetworkSummaryStats;
              if (newData) {
                setData(transformDbDataToNetworkSummary(newData));
                setLastFetchTimestamp(Date.now());
                console.log('[NetworkContext] Network data updated via realtime');
              }
            }
          }
        )
        .subscribe((status, err) => {
          if (!mounted) return;
          if (err) {
            console.error('[NetworkContext] Realtime subscription error:', err);
          } else if (status === 'SUBSCRIBED') {
            console.log('[NetworkContext] Successfully subscribed to network_summary_stats updates');
          }
        });
    };

    setupSubscription();

    return () => {
      mounted = false;
      if (channel) {
        // Use timeout to ensure cleanup happens after any pending operations
        const channelToRemove = channel;
        setTimeout(() => {
          supabase.removeChannel(channelToRemove).catch(err => 
            console.error('[NetworkContext] Failed to remove channel:', err)
          );
        }, 0);
      }
    };
  }, [fetchData]);

  // Periodic refetch if data is stale and tab is visible (as fallback)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[NetworkContext] Data is stale, refetching due to interval...');
          fetchData();
        }
      }
    }, REFETCH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [lastFetchTimestamp, fetchData]);

  // Refetch on window focus if data is stale (as fallback)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[NetworkContext] Tab focused and data is stale, refetching...');
          fetchData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastFetchTimestamp, fetchData]);

  return (
    <NetworkContext.Provider value={{ data, loading, error }}>
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetwork() {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}